import { useQuery } from '@tanstack/react-query';
import * as React from 'react';
import { diContainer } from 'src/dependencyInjection/diContainer';
import { DI_TYPES } from 'src/dependencyInjection/diTypes';
import { IHttpService } from 'src/services/HttpService/IHttpService';
import { z } from 'zod';

export interface ICarVinDecoderProps {
  vin: string | undefined | null;
}

export const CarVinDecoder: React.FunctionComponent<
  ICarVinDecoderProps
> = props => {
  const { carInfoToShow, error, isLoading, isVinInvalid } = useGetCarVinDecode(
    props.vin ?? ''
  );
  if (!props.vin) {
    return <div>Not a valid vin</div>;
  }
  if (isLoading) {
    return <div>Decoding car vin</div>;
  } else if (error) {
    return <div>Error decoding car vin</div>;
  } else if (isVinInvalid) {
    return <div>Invalid VIN</div>;
  } else {
    return (
      <div className='flex flex-col gap-3 overflow-x-hidden'>
        {carInfoToShow?.map(result => {
          return (
            <div key={result.VariableId} className='grid grid-cols-2 gap-3'>
              <div className='text-gray-600'>{result.Variable}</div>
              <div>{result.Value}</div>
            </div>
          );
        })}
      </div>
    );
  }
};

function useGetCarVinDecode(vin: string) {
  const { data, error, isLoading } = useQuery({
    queryKey: [vin],
    queryFn: async () => {
      if (!vin) {
        return undefined;
      }
      const url = `https://vpic.nhtsa.dot.gov/api/vehicles/DecodeVin/${vin}?format=json`;
      const response = await diContainer
        .get<IHttpService>(DI_TYPES.HttpService)
        .send({
          type: 'GET',
          url,
          response: z.object({
            Count: z.number(),
            Message: z.string(),
            SearchCriteria: z.string(),
            Results: z.array(
              z.object({
                Value: z.string().nullish(),
                ValueId: z.string().nullish(),
                Variable: z.string(),
                VariableId: z.number(),
              })
            ),
          }),
        });
      return response;
    },
  });
  return {
    carInfoToShow: data?.Results.filter(result => {
      switch (result.Variable) {
        case 'Error Code':
        case 'Suggested VIN':
        case 'Possible Values':
        case 'Additional Error Text':
        case 'Error Text':
          return false;
      }
      return true;
    }),
    error,
    isLoading,
    isVinInvalid: Boolean(
      data?.Results.find(result => result.Variable === 'Error Code')?.Value !==
        '0'
    ),
  };
}
